<template>
    <div class="h-screen bg-slate-50 px-8 py-8 flex flex-col z-40 overflow-hidden font-sans">
        
        <header class="flex items-end justify-between mb-8">
            <div>
                <h1 class="text-4xl font-black text-slate-900 tracking-tighter">
                    <span class="text-blue-600 font-light">Vanguardia</span>Dashboard
                </h1>
                <p class="text-slate-500 font-medium mt-1 uppercase tracking-widest text-xs">
                    Bienvenido, 
                    <span class="capitalize text-slate-800 font-bold">
                        {{ (usuarioLogeado?.nombre || usuarioLogeado?.usr_nombre || usuarioLogeado?.name || 'Administrador').toLowerCase() }}
                    </span>
                    • {{ fechaLarga }}
                </p>
            </div>
            <div class="bg-white p-3 rounded-2xl shadow-sm border border-slate-100">
                <faIcon icon="shield-alt" type="fas" class="text-blue-500 text-xl" />
            </div>
        </header>

        <div class="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-8 mb-4">
            
            <div class="rounded-xl p-12 flex flex-col items-center justify-center text-white shadow-2xl relative overflow-hidden" style="background-color: #274d86;">
                <div class="relative z-10 flex flex-col items-center pointer-events-none">
                    <span class="text-blue-200 text-xs font-bold uppercase tracking-[0.5em] mb-8">Hora del Sistema</span>
                    
                    <RelojDigital class="reloj-hero text-8xl xl:text-9xl font-mono font-black tracking-tighter text-white tabular-nums" />
                    
                    <div class="mt-10 h-1.5 w-32 bg-blue-400 rounded-full opacity-30"></div>
                </div>
            </div>

            <div class="rounded-xl p-10 shadow-xl flex flex-col justify-between" style="background-color: #274d86;">
                
                <div class="flex justify-between items-start">
                    <div>
                        <h2 class="text-blue-200 font-bold uppercase tracking-wider text-sm">Ventas del Día</h2>
                        <p class="text-6xl xl:text-7xl font-black text-white tracking-tighter mt-2">
                            {{ `$ ${formatearCifra(totalVentasFinal)}` }}
                        </p>
                    </div>
                    <div class="p-4 bg-white text-blue-800 rounded-3xl shadow-sm flex items-center justify-center">
                        <faIcon icon="cash-register" type="fas" class="text-3xl" style="color: #274d86;" />
                    </div>
                </div>

                <div class="grid grid-cols-2 gap-4 mt-8">
                    <div class="bg-white p-6 rounded-xl shadow-sm">
                        <span class="text-slate-500 font-bold text-xs uppercase block mb-1">Efectivo</span>
                        <p class="text-3xl font-black text-slate-800">{{ `$ ${formatearCifra(ventasEfectivo)}` }}</p>
                    </div>

                    <div class="bg-white p-6 rounded-xl shadow-sm">
                        <span class="text-slate-500 font-bold text-xs uppercase block mb-1">Transbank</span>
                        <p class="text-3xl font-black text-slate-800">{{ `$ ${formatearCifra(ventasTransbank)}` }}</p>
                    </div>

                    <div class="col-span-2 bg-white p-5 rounded-xl shadow-sm flex justify-around">
                        <div class="text-center w-1/2 border-r border-slate-200">
                            <p class="text-[10px] text-slate-400 font-bold uppercase">Cheque</p>
                            <p class="text-lg font-bold text-slate-700">{{ `$ ${formatearCifra(ventasCheque)}` }}</p>
                        </div>
                        <div class="text-center w-1/2">
                            <p class="text-[10px] text-slate-400 font-bold uppercase">Convenio</p>
                            <p class="text-lg font-bold text-slate-700">{{ `$ ${formatearCifra(ventasConvenio)}` }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import RelojDigital from '@/components/reloj/RelojDigital';
import { useStore } from 'vuex';
import { computed, onMounted, ref } from 'vue';
import moment from "moment";
import "moment/locale/es";
import { separarPorMiles } from '@/utils/helpers';
import VentaService from '@/services/VentasService';
import OTService from '@/services/OrdenTrabajoService';
import AbonoService from '@/services/AbonoService';

export default {
    name: 'Home',
    components: { RelojDigital },
    setup() {
        const store = useStore();
        moment.locale("es");

        const dataVentas = ref({ bazar: [], abonos: [], ots: [] });
        const sucursalId = computed(() => store.getters["auth/authUser"]?.sucursal?.id || 1);

        const cargarDatos = async () => {
            const fecha = moment().format('DD-MM-YYYY'); 
            try {
                const [resOts, resAbonos, resBazar] = await Promise.all([
                    OTService.getReporteOtDiario(fecha),
                    AbonoService.getAbonosDiarios(fecha),
                    VentaService.getVentasDiarias(fecha)
                ]);
                dataVentas.value.ots = resOts?.data?.[sucursalId.value] || [];
                dataVentas.value.abonos = resAbonos?.data?.[sucursalId.value] || [];
                dataVentas.value.bazar = resBazar?.data?.[sucursalId.value] || [];
            } catch (e) { console.error(e); }
        };

        onMounted(cargarDatos);

        const ventasEfectivo = computed(() => {
            const b = (dataVentas.value.bazar || []).filter(v => v.metodoPago?.metodo === 'Efectivo').reduce((a, c) => a + Number(c.vnt_total || 0), 0);
            const a = (dataVentas.value.abonos || []).filter(v => v.medioPago === 'Efectivo').reduce((a, c) => a + Number(c.montoAbono || c.ab_abono || 0), 0);
            return b + a;
        });

        const ventasTransbank = computed(() => {
            const b = (dataVentas.value.bazar || []).filter(v => v.metodoPago?.metodo === 'Transbank').reduce((a, c) => a + Number(c.vnt_total || 0), 0);
            const a = (dataVentas.value.abonos || []).filter(v => v.medioPago === 'Transbank').reduce((a, c) => a + Number(c.montoAbono || c.ab_abono || 0), 0);
            return b + a;
        });

        const ventasCheque = computed(() => (dataVentas.value.abonos || []).filter(v => v.medioPago === 'Cheque').reduce((a, c) => a + Number(c.montoAbono || c.ab_abono || 0), 0));
        const ventasConvenio = computed(() => (dataVentas.value.abonos || []).filter(v => v.medioPago === 'Convenio').reduce((a, c) => a + Number(c.montoAbono || c.ab_abono || 0), 0));

        const totalVentasFinal = computed(() => {
            const totalBazar = (dataVentas.value.bazar || []).reduce((a, c) => a + Number(c.vnt_total || 0), 0);
            const totalOTs = (dataVentas.value.ots || []).reduce((a, c) => a + Number(c.total_final || c.ot_valor_total || c.valorTotal || 0), 0);
            return totalBazar + totalOTs;
        });

        return {
            usuarioLogeado: computed(() => store.getters["auth/authUser"]),
            fechaLarga: computed(() => moment().format("dddd, D [de] MMMM [de] YYYY")),
            ventasEfectivo, ventasTransbank, ventasCheque, ventasConvenio, totalVentasFinal,
            formatearCifra: (val) => separarPorMiles(val)
        }
    }
}
</script>

<style scoped>
html, body { overflow: hidden; }
.capitalize { text-transform: capitalize; }

/* Limpieza total del componente RelojDigital */
::v-deep(.reloj-hero), 
::v-deep(.reloj-hero div), 
::v-deep(.reloj-hero span),
::v-deep(.reloj-hero p) {
    background-color: transparent !important;
    color: #ffffff !important;
    box-shadow: none !important;
    border: none !important;
    transform: none !important;
}

/* Ocultar cualquier texto secundario que el componente RelojDigital traiga internamente */
::v-deep(.reloj-hero .text-sm), 
::v-deep(.reloj-hero .text-xs),
::v-deep(.reloj-hero p:not(.text-7xl):not(.text-8xl):not(.text-9xl)) {
    display: none !important;
}
</style>